// src/components/DataAuditPage.js

import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlayCircle, faSpinner, faCheckCircle, faExclamationTriangle,
  faClipboardList, faArrowLeft, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';

import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';

import './DataAuditPage.css';

const generateReportCallable = httpsCallable(functions, 'generateDataIntegrityReport');
const AUTHORIZED_EMAIL = "jonstewart24@gmail.com";

function DataAuditPage() {
  const { currentUser, loadingAuth } = useAuth();

  const [reportResults, setReportResults] = useState(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [error, setError] = useState(null);
  const [userMessage, setUserMessage] = useState({ type: '', text: '' });

  const handleRunReport = async () => {
    setIsLoadingReport(true);
    setError(null);
    setUserMessage({ type: '', text: '' });
    setReportResults(null);

    try {
      if (!currentUser) {
        setError("No active user session. Please log in again.");
        setIsLoadingReport(false);
        return;
      }

      // Client-side authorization check (primary guard is server-side)
      if (currentUser.email?.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) {
        throw new Error("You are not authorized to perform this action.");
      }

      // Force refresh ID token
      try {
        await currentUser.getIdToken(true);
      } catch (tokenError) {
        console.error("Error refreshing ID token:", tokenError);
        setError("Failed to refresh authentication token. Please try logging out and back in.");
        setIsLoadingReport(false);
        return;
      }

      const result = await generateReportCallable();
      const data = result.data;

      if (data && data.success) {
        setReportResults(data);
        setUserMessage({ type: 'success', text: data.message || 'Report generated successfully.' });
      } else {
        throw new Error(data?.message || 'Failed to generate report.');
      }
    } catch (err) {
      console.error("Error during report generation:", err);
      let displayError = "An unknown error occurred.";
      if (err.code && err.message) {
        displayError = `Error (${err.code}): ${err.message}`;
      } else if (err.message) {
        displayError = err.message;
      }
      setError(displayError);
      setUserMessage({ type: 'danger', text: displayError });
    } finally {
      setIsLoadingReport(false);
    }
  };

  // --- Render Logic ---

  if (loadingAuth) {
    return (
      <div className="data-audit-page">
        <div className="data-audit-loading">
          <div className="skeleton-header"></div>
          <div className="skeleton-card"></div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Access control - only allow authorized email
  if (currentUser.email?.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) {
    return (
      <div className="data-audit-page">
        <div className="data-audit-header">
          <div className="container">
            <div className="data-audit-header-content">
              <div className="data-audit-header-left">
                <Link to="/dashboard" className="data-audit-back-btn">
                  <FontAwesomeIcon icon={faArrowLeft} />
                </Link>
                <h1 className="data-audit-title">Access Denied</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="data-audit-content">
            <div className="audit-alert audit-alert-danger">
              <FontAwesomeIcon icon={faExclamationTriangle} className="audit-alert-icon" />
              <div className="audit-alert-content">
                You do not have permission to view this page.
              </div>
            </div>
            <Link to="/dashboard" className="audit-run-btn" style={{ textDecoration: 'none' }}>
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="data-audit-page">
      {/* Page Header */}
      <div className="data-audit-header">
        <div className="container">
          <div className="data-audit-header-content">
            <div className="data-audit-header-left">
              <Link to="/dashboard" className="data-audit-back-btn" title="Back to Dashboard">
                <FontAwesomeIcon icon={faArrowLeft} />
              </Link>
              <div>
                <h1 className="data-audit-title">
                  <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: '0.5rem' }} />
                  Data Integrity Audit
                </h1>
                <p className="data-audit-subtitle">Scan database for integrity issues</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="data-audit-content">
          {/* Alerts */}
          {userMessage.text && (
            <div className={`audit-alert audit-alert-${userMessage.type || 'info'}`}>
              {userMessage.type === 'success' && <FontAwesomeIcon icon={faCheckCircle} className="audit-alert-icon" />}
              {(userMessage.type === 'danger' || userMessage.type === 'warning') && <FontAwesomeIcon icon={faExclamationTriangle} className="audit-alert-icon" />}
              <div className="audit-alert-content">{userMessage.text}</div>
              <button className="audit-alert-close" onClick={() => setUserMessage({ type: '', text: '' })}>
                &times;
              </button>
            </div>
          )}

          {error && !userMessage.text && (
            <div className="audit-alert audit-alert-danger">
              <FontAwesomeIcon icon={faExclamationTriangle} className="audit-alert-icon" />
              <div className="audit-alert-content">{error}</div>
            </div>
          )}

          {/* Run Report Card */}
          <div className="audit-run-card">
            <div className="audit-run-card-body">
              <div className="audit-run-icon">
                <FontAwesomeIcon icon={faClipboardList} />
              </div>
              <h2 className="audit-run-title">Data Integrity Report</h2>
              <p className="audit-run-description">
                Scan the database and authentication system for potential data integrity issues, orphaned records, and inconsistencies.
              </p>
              <button
                className="audit-run-btn"
                onClick={handleRunReport}
                disabled={isLoadingReport}
              >
                {isLoadingReport ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Generating Report...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlayCircle} />
                    Run Data Integrity Report
                  </>
                )}
              </button>
              {isLoadingReport && (
                <p className="audit-run-note">This may take several minutes depending on data size.</p>
              )}
            </div>
          </div>

          {/* Loading State */}
          {isLoadingReport && !reportResults && (
            <div className="audit-loading-state">
              <div className="audit-loading-spinner"></div>
              <p className="audit-loading-text">Fetching and analyzing data, please wait...</p>
            </div>
          )}

          {/* Results */}
          {reportResults && !isLoadingReport && (
            <div className="audit-results">
              <div className="audit-results-header">
                <h3 className="audit-results-title">
                  <FontAwesomeIcon icon={faClipboardList} />
                  Report Results
                </h3>
              </div>
              <div className="audit-results-body">
                {/* Summary */}
                <div className="audit-summary">
                  <div className="audit-summary-card">
                    <p className={`audit-summary-value ${reportResults.totalIssues > 0 ? 'audit-summary-value-danger' : 'audit-summary-value-success'}`}>
                      {reportResults.totalIssues}
                    </p>
                    <p className="audit-summary-label">Total Issues Found</p>
                  </div>
                </div>

                {reportResults.totalIssues > 0 ? (
                  <>
                    {/* Issues by Category */}
                    <div className="audit-categories">
                      <h4 className="audit-categories-title">Issues by Category</h4>
                      <div className="audit-category-list">
                        {Object.entries(reportResults.issuesByCollection)
                          .filter(([, count]) => typeof count === 'number' && count > 0)
                          .map(([collectionName, count]) => (
                            <div key={collectionName} className="audit-category-item">
                              {collectionName.replace(/([A-Z])/g, ' $1').trim()}
                              <span className="audit-category-count">{count}</span>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Detailed Issues Table */}
                    <h4 className="audit-categories-title">Detailed Issues</h4>
                    <div className="audit-table-container">
                      <table className="audit-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Category</th>
                            <th>Document ID</th>
                            <th>Reason</th>
                            <th>Org ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.isArray(reportResults.issues) && reportResults.issues.map((issue, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{issue.collectionName}</td>
                              <td className="audit-table-id">{issue.documentId}</td>
                              <td className="audit-table-reason">{issue.reason}</td>
                              <td className="audit-table-id">{issue.problematicOrgId || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="audit-success-state">
                    <div className="audit-success-icon">
                      <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <h4 className="audit-success-title">All Clear!</h4>
                    <p className="audit-success-text">No data integrity issues found. Everything looks good!</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DataAuditPage;
